import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { BackButton } from '../components/back-button'
import { useQuery } from '@tanstack/react-query'
import { Spinner, apiClient, ApiClientError, useCurrentUser, cn, useToast } from '@dogdisk/shared'
import { AppLayout } from '../components/app-layout'
import { MobileScreen } from '../components/mobile-screen'
import { TicketActionsSheet } from '../components/modals/ticket-actions-sheet'
import type { Ticket, Message } from '../_lib/messaging-api'

// server 端 GET `/gapi/user/tickets/{id}` 只返 {ticket},消息走 /gapi/user/conversations/{id}/messages
// 单独请求。ticket.state(server 用 state 不是 status)。

const STATE_LABEL: Record<string, string> = {
  open: '待处理',
  in_progress: '进行中',
  pending: '待客户',
  resolved: '已解决',
  closed: '已关闭',
}

const STATE_CLASS: Record<string, string> = {
  open: 'bg-[#FEF3C7] text-[#F59E0B]',
  in_progress: 'bg-[#ebfaf6] text-[#19caad]',
  pending: 'bg-[#FEF3C7] text-[#F59E0B]',
  resolved: 'bg-[#DBEAFE] text-[#3B82F6]',
  closed: 'bg-[#F1F2F4] text-[#9CA3AF]',
}

export function TicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>()
  const { user } = useCurrentUser()
  const ticketQuery = useQuery({
    queryKey: ['ticket', ticketId] as const,
    enabled: Boolean(ticketId),
    // gapi-restful-regroup:GET /gapi/user/tickets/{ticket_id}
    queryFn: () =>
      apiClient.get<{ ticket: Ticket }>(`/gapi/user/tickets/${ticketId!}`),
  })
  const ticket = ticketQuery.data?.ticket
  // messages 走 /gapi/user/conversations/{conversation_id}/messages(conv_id 从 ticket 里拿)
  const msgsQuery = useQuery({
    queryKey: ['ticket-messages', ticket?.conversation_id] as const,
    enabled: Boolean(ticket?.conversation_id),
    queryFn: () =>
      apiClient.get<{ messages: Message[] }>(
        `/gapi/user/conversations/${ticket!.conversation_id}/messages`,
      ),
  })
  const isLoading = ticketQuery.isLoading || msgsQuery.isLoading
  const refetch = async () => {
    await msgsQuery.refetch()
  }
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [moreOpen, setMoreOpen] = useState(false)
  const toast = useToast()

  // B3-#8 s4Mv2P 工单 moreBtn:复用 resolve / close / reopen RPC
  const callAction = async (path: string, successMsg: string) => {
    if (!ticketId) return
    setBusy(true)
    setError(null)
    setMoreOpen(false)
    try {
      await apiClient.post(path, {})
      toast.push(successMsg, { variant: 'success' })
      await refetch()
    } catch (e) {
      if (e instanceof ApiClientError) setError(e.message || '操作失败')
      else setError(e instanceof Error ? e.message : '操作失败')
    } finally {
      setBusy(false)
    }
  }

  const onReply = async () => {
    if (!ticketId || !draft.trim()) return
    setBusy(true)
    setError(null)
    try {
      // gapi-restful-regroup:POST /gapi/user/tickets/{ticket_id}/reply;body 仅 content
      await apiClient.post(`/gapi/user/tickets/${ticketId}/reply`, { content: draft })
      setDraft('')
      await refetch()
    } catch (e) {
      setError(e instanceof Error ? e.message : '发送失败')
    } finally {
      setBusy(false)
    }
  }

  const closed = ticket?.state === 'closed' || ticket?.state === 'resolved'

  return (
    <AppLayout>
      <MobileScreen bg="surface">
        <header data-testid="WjxGL" className="flex items-center justify-between gap-3 border-b border-[#F3F4F6] bg-white px-6 pt-[44px] pb-3">
          <BackButton className="shrink-0" />
          <h1 className="flex-1 truncate text-center text-[16px] font-semibold text-[#1A1A1A]">对话详情</h1>
          <button
            type="button"
            data-testid="WjxGL-more-actions"
            onClick={() => setMoreOpen((v) => !v)}
            aria-label="更多操作"
            className="relative flex h-9 w-9 items-center justify-center rounded-full bg-[#F6F7F8]"
          >
            <svg width="4" height="16" viewBox="0 0 4 16" fill="#1A1A1A"><circle cx="2" cy="2" r="1.5" /><circle cx="2" cy="8" r="1.5" /><circle cx="2" cy="14" r="1.5" /></svg>
          </button>
        </header>

        {/* D6 s4Mv2P 工单更多操作 BottomSheet(替代原内嵌菜单)*/}
        {ticket && (
          <TicketActionsSheet
            open={moreOpen}
            onClose={() => setMoreOpen(false)}
            ticketId={ticket.id}
            ticketNumber={ticket.ticket_number || ticket.id.slice(0, 8)}
            ticketState={ticket.state}
            onActionDone={() => {
              void ticketQuery.refetch()
              void msgsQuery.refetch()
            }}
          />
        )}

        {/* reopen 仍走旧 callAction 内嵌按钮(state=closed 显独立);
            ticket-actions-sheet 主要覆盖 open / in_progress / resolved 三态的 4 主 action */}
        {ticket && ticket.state === 'closed' && moreOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/40"
            onClick={() => setMoreOpen(false)}
          >
            <div
              className="mx-auto mt-[88px] w-[200px] max-w-[90vw] translate-x-[80px] rounded-lg bg-white shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                disabled={busy}
                onClick={() => callAction(`/gapi/user/tickets/${ticketId}/reopen`, '工单已重新打开')}
                className="flex w-full items-center gap-2 px-4 py-3 text-left text-[13px] text-[#1A1A1A] active:bg-[#F6F7F8] disabled:opacity-50"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1971ca" strokeWidth="2"><path d="M3 12a9 9 0 0114-7M21 3v6h-6M21 12a9 9 0 01-14 7M3 21v-6h6" /></svg>
                重新打开
              </button>
            </div>
          </div>
        )}

        {ticket && (
          <section className="bg-white px-6 py-3 border-b border-[#F3F4F6]">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-[12px] font-mono text-[#9CA3AF]"># {ticket.ticket_number || ticket.id.slice(0, 8)}</p>
              <p className="text-[12px] text-[#9CA3AF]">
                创建于 {ticket.created_at.slice(5, 16).replace('T', ' ')}
              </p>
            </div>
            <p className="mt-1 text-[14px] font-semibold text-[#1A1A1A]">{ticket.title}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-[#9CA3AF]">工单状态：</span>
              <span
                className={cn(
                  'rounded-md px-2 py-0.5 text-[11px] font-medium',
                  STATE_CLASS[ticket.state] ?? 'bg-[#F1F2F4] text-[#9CA3AF]',
                )}
              >
                {STATE_LABEL[ticket.state] ?? ticket.state}
              </span>
              <button type="button" className="ml-auto rounded-md bg-[#F6F7F8] px-2 py-0.5 text-[11px] text-[#9CA3AF]">
                标记已解决
              </button>
            </div>
          </section>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {isLoading ? (
            <div className="flex justify-center py-8"><Spinner className="text-gray-400" /></div>
          ) : (
            <ul className="flex flex-col gap-3">
              {(msgsQuery.data?.messages ?? []).map((m) => {
                const mine = m.sender_id === user?.user_id
                return (
                  <li key={m.id} className={`flex items-start gap-2 ${mine ? 'flex-row-reverse' : 'flex-row'}`}>
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold text-white ${mine ? 'bg-[#1971ca]' : 'bg-[#19caad]'}`}
                    >
                      {(mine ? '我' : '客').slice(0, 1)}
                    </span>
                    <div className="max-w-[78%]">
                      <p className="mb-0.5 text-[10px] text-[#9CA3AF]">
                        {mine ? '我' : '客服'} · {m.created_at.slice(5, 16).replace('T', ' ')}
                      </p>
                      <div
                        className={
                          mine
                            ? 'rounded-2xl rounded-tr-md bg-[#19caad] px-3 py-2 text-[13px] text-white'
                            : 'rounded-2xl rounded-tl-md bg-[#FEF7CD] px-3 py-2 text-[13px] text-[#1A1A1A]'
                        }
                      >
                        {m.content}
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {!closed && (
          <footer
            className="border-t border-[#F3F4F6] bg-white px-4 pt-3"
            style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
          >
            {error && <p className="mb-1 text-[12px] text-[#FF6B6B]">{error}</p>}
            <p className="mb-2 text-[11px] text-[#9CA3AF]">回复此对话</p>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="输入回复内容…"
              rows={3}
              className="mb-2 w-full resize-none rounded-lg bg-[#F6F7F8] p-3 text-[13px] outline-none placeholder:text-[#9CA3AF]"
            />
            <div className="flex items-center justify-between">
              <button type="button" className="flex items-center gap-1 text-[12px] text-[#9CA3AF]">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12V8a2 2 0 00-2-2h-3l-2-2H10L8 6H5a2 2 0 00-2 2v10a2 2 0 002 2h7" /><circle cx="17" cy="17" r="3" /></svg>
                附件
              </button>
              <button
                type="button"
                onClick={onReply}
                disabled={busy || !draft.trim()}
                className="flex items-center gap-1 rounded-lg bg-[#19caad] px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z" /></svg>
                发送
              </button>
            </div>
          </footer>
        )}
      </MobileScreen>
    </AppLayout>
  )
}
